// import type { Config } from '@plone/volto/types/config'; // Config type not available
import installSettings from './config/settings';
import paintBucketSVG from '@plone/volto/icons/paint.svg';
import heroSVG from '@plone/volto/icons/hero.svg';
import cardsSVG from '@plone/volto/icons/cards.svg';
import iconSVG from '@plone/volto/icons/select-all.svg';
import sliderSVG from '@plone/volto/icons/slider.svg';
import calendarSVG from '@plone/volto/icons/calendar.svg';
import { Edit as CustomHeroEdit, View as CustomHeroView,} from './components/Blocks/CustomHero';
import { Edit as ButtonRowEdit, View as ButtonRowView,} from './components/Blocks/ButtonRow';
import { Edit as MultiCardEdit, View as MultiCardView,} from './components/Blocks/MultiCardBlock';
import { Edit as IconLinkRowEdit, View as IconLinkRowView,} from './components/Blocks/IconLinkRow';
import { Edit as EmblaCarouselEdit, View as EmblaCarouselView,} from './components/Blocks/EmblaCarousel';
import { Edit as EventsEdit, View as EventsView,} from './components/Blocks/Events';
import { buildRestriction } from './config/blockRestrictions';
import blockThemes from './config/blockThemes';
import { injectEditThemeStyles } from './utils/injectEditThemeStyles';
import SocialSharing from '@codesyntax/volto-social-sharing/SocialSharing';
import { DEFAULT_SOCIAL } from '@codesyntax/volto-social-sharing/defaultSettings';
import { faSquareXTwitter } from '@fortawesome/free-brands-svg-icons';
import { library } from '@fortawesome/fontawesome-svg-core';

import FileView from './components/Views/File';
export { FileView };

library.add(faSquareXTwitter);

type Config = any;

// Wrapper to filter srcset and prevent small images from being selected
const CustomImage = (props) => {
  const { item, imageField = 'image', ...rest } = props;
  
  // If we have image_scales, filter out scales that are too small
  if (item?.image_scales?.[imageField]) {
    const scales = item.image_scales[imageField];
    
    // Create a filtered version that excludes very small scales
    const filteredScales = scales.map(scale => {
      if (scale.scales) {
        // Filter out scales smaller than 128px width to prevent pixelation
        const filteredScaleEntries = Object.entries(scale.scales).filter(
          ([key, scaleData]) => scaleData.width >= 128
        );
        
        return {
          ...scale,
          scales: Object.fromEntries(filteredScaleEntries)
        };
      }
      return scale;
    });
    
    const modifiedItem = {
      ...item,
      image_scales: {
        ...item.image_scales,
        [imageField]: filteredScales
      }
    };
    
    return <ImageComponent {...rest} item={modifiedItem} imageField={imageField} />;
  }
  
  return <ImageComponent {...props} />;
};

function applyConfig(config: Config): Config {
  installSettings(config);

  config.settings.isMultilingual = false;
  config.settings.supportedLanguages = ['en'];
  config.settings.defaultLanguage = 'en';

  config.blocks.themes = blockThemes;

  if (typeof window !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener(
        'DOMContentLoaded',
        () => injectEditThemeStyles(blockThemes),
        { once: true },
      );
    } else {
      injectEditThemeStyles(blockThemes);
    }
  }

  // Enable autoplay on Slider
  if (config.blocks.blocksConfig.slider) {
    config.blocks.blocksConfig.slider.enableAutoPlay = true;
  }

  // Adding custom colours and block styles to Slate editor
  config.settings.slate.styleMenu.inlineStyles = [
    ...(config.settings.slate.styleMenu?.inlineStyles || []),
    {
      cssClass: 'text-red',
      label: 'Red Text',
      icon: paintBucketSVG,
    },
    {
      cssClass: 'text-green',
      label: 'Green Text',
      icon: paintBucketSVG,
    },
    {
      cssClass: 'text-blue',
      label: 'Blue Text',
      icon: paintBucketSVG,
    },
  ];

  config.settings.slate.styleMenu.blockStyles = [
    ...(config.settings.slate.styleMenu?.blockStyles || []),
    {
      cssClass: 'highlight-block',
      label: 'Highlighted Block',
      nodeType: 'paragraph',
      icon: paintBucketSVG,
    },
  ];

  // CustomHero
  config.blocks.blocksConfig.customHero = {
    id: 'customHero',
    title: 'Hero Block',
    icon: heroSVG,
    group: 'common',
    view: CustomHeroView,
    edit: CustomHeroEdit,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };

  // ButtonRow
  config.blocks.blocksConfig.buttonRow = {
    id: 'buttonRow',
    title: 'Button Row',
    icon: cardsSVG,
    group: 'common',
    view: ButtonRowView,
    edit: ButtonRowEdit,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };

  // IconLinkRow
  config.blocks.blocksConfig.iconLinkRow = {
    id: 'iconLinkRow',
    title: 'Icon Link Row',
    icon: iconSVG,
    group: 'common',
    view: IconLinkRowView,
    edit: IconLinkRowEdit,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };

  // Multicard Block
  config.blocks.blocksConfig.multiCard = {
    id: 'multiCard',
    title: 'Multi Card',
    icon: cardsSVG,
    group: 'common',
    view: MultiCardView,
    edit: MultiCardEdit,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };

  // Embla Carousel
  config.blocks.blocksConfig.EmblaCarousel = {
    id: 'EmblaCarousel',
    title: 'Embla Carousel',
    icon: sliderSVG,
    group: 'common',
    view: EmblaCarouselView,
    edit: EmblaCarouselEdit,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };

  // Events block for listing and carousel
  config.blocks.blocksConfig.Events = {
    id: 'Events',
    title: 'Events',
    icon: calendarSVG,
    group: 'common',
    view: EventsView,
    edit: EventsEdit,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };

//Volto Social Sharing
  DEFAULT_SOCIAL.push({
    name: "LinkedIn",
    fa_name: ["fab", "linkedin-in"],
    color: "#0a66c2",
    sharing_url: "https://www.linkedin.com/sharing/share-offsite/?url=",
    id: "li"
  });
  let twitter = DEFAULT_SOCIAL.find((social) => social.id === 'tw');
  if (twitter) {
    twitter.fa_name = faSquareXTwitter;
    twitter.color = 'black';
    twitter.sharing_url = "https://x.com/intent/post?text=Check%20this%20out:&url=";
;
  }
  config.settings.appExtras = [
    ...config.settings.appExtras,
    {
      match: {
        path: ['**/news/**', '**/events/**', '**/engagement/**'],
        exact: false,
      },
      component: SocialSharing,
      props: {
        socialElements: DEFAULT_SOCIAL,
      },
    },
  ];

 //File views
  config.views = {
    ...config.views,
    contentTypesViews: {
      File: FileView,
    },
  };

  //Configure which blocks show and for which Editors (Keep last in line, after block registrations)
  const blocksConfig = config.blocks.blocksConfig;
  Object.keys(blocksConfig).forEach((blockId) => {
    const originalRestricted = blocksConfig[blockId].restricted;
    blocksConfig[blockId] = {
      ...blocksConfig[blockId],
      restricted: buildRestriction(blockId, originalRestricted),
    };
  });

  return config;
}

export default applyConfig;
